/**
 * Displaying hashes and byte strings.
 *
 * House rule for this app: a sha256 is never truncated. All 64 characters are
 * always on screen. A hash you can only see half of teaches nothing, and the
 * whole point here is that the user can compare what they built against what
 * the spec and the server say.
 */

import { useState, type ReactNode } from "react";
import { Check, Copy } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * `value` may be a function, and for anything large it should be.
 *
 * Copying a 2 MiB blob means a 4 million character hex string. Passing that
 * eagerly rebuilds it on every render of the surrounding component — switching
 * between chunks froze the tab on exactly this. A getter defers the work to the
 * click, where it is a one-off cost nobody notices.
 */
function CopyButton({ value, label }: { value: string | (() => string); label: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <button
      type="button"
      aria-label={`Copy ${label}`}
      title={`Copy ${label}`}
      onClick={() => {
        const resolved = typeof value === "function" ? value() : value;
        void navigator.clipboard.writeText(resolved).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        });
      }}
      className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      {copied ? <Check className="size-3.5 text-emerald-500" /> : <Copy className="size-3.5" />}
    </button>
  );
}

export type HashTone = "default" | "key" | "hash" | "muted";

const TONE_CLASSES: Record<HashTone, string> = {
  default: "text-foreground",
  // Keys are bearer secrets, so they get a colour that reads as "handle with care".
  key: "text-amber-600 dark:text-amber-400",
  hash: "text-sky-700 dark:text-sky-300",
  muted: "text-muted-foreground",
};

interface HashValueProps {
  value: string;
  tone?: HashTone;
  className?: string;
}

/**
 * A full, wrapping, monospace value. Never truncated, never ellipsised.
 *
 * `select-all` means one click selects the entire value. Without it, a
 * double-click on a wrapped 64 character hash grabs only the run of digits
 * under the cursor, which is exactly the wrong thing to paste somewhere.
 */
export function HashValue({ value, tone = "default", className }: HashValueProps) {
  return (
    <code className={cn("font-mono text-xs leading-relaxed break-all select-all", TONE_CLASSES[tone], className)}>
      {value}
    </code>
  );
}

interface FieldProps {
  label: ReactNode;
  value: string;
  tone?: HashTone;
  /** Shown under the value, e.g. what the field means. */
  hint?: ReactNode;
  copyable?: boolean;
}

/**
 * One labelled value in a two-column grid.
 *
 * The label column is fixed and narrow so the value column always has room for
 * 64 hex characters without wrapping awkwardly.
 */
export function Field({ label, value, tone = "default", hint, copyable = true }: FieldProps) {
  return (
    <div className="grid grid-cols-[7.5rem_1fr] items-start gap-x-3 gap-y-1 py-1.5">
      <div className="pt-0.5 text-xs font-medium text-muted-foreground">{label}</div>
      <div className="min-w-0">
        <div className="flex items-start gap-1">
          <HashValue value={value} tone={tone} className="min-w-0 flex-1" />
          {copyable && <CopyButton value={value} label={typeof label === "string" ? label : "value"} />}
        </div>
        {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
      </div>
    </div>
  );
}

/** A group of fields sharing one bordered panel. */
export function FieldGroup({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("divide-y divide-border/60 rounded-lg border bg-muted/30 px-3 py-1", className)}>{children}</div>
  );
}

const HEX_OCTETS = Array.from({ length: 256 }, (_, byte) => byte.toString(16).padStart(2, "0"));

function toHexString(bytes: Uint8Array): string {
  // Table lookup into an array, joined once. Repeated `+=` over a megabyte of
  // input is what made copying a large chunk expensive in the first place.
  const parts = new Array<string>(bytes.length);
  for (let i = 0; i < bytes.length; i++) parts[i] = HEX_OCTETS[bytes[i]!]!;
  return parts.join("");
}

/**
 * How much *content* a preview will render.
 *
 * Content and hashes are different things and get different rules. A hash is 32
 * bytes and is always shown in full — that is the point of this app. File
 * content is unbounded: at a 2 MiB chunk size a single chunk is the whole file,
 * and rendering millions of hex characters locks up the tab. So previews of
 * content are capped, and say so.
 */
const PREVIEW_BYTE_LIMIT = 512;
const PREVIEW_TEXT_LIMIT = 2000;

/**
 * Raw bytes as hex, with the byte count.
 *
 * Anything past the preview limit is dropped from the DOM, not merely scrolled:
 * the copy button still yields the complete value.
 */
export function ByteView({
  bytes,
  label,
  maxHeight = "10rem",
  limit = PREVIEW_BYTE_LIMIT,
}: {
  bytes: Uint8Array;
  label?: string;
  maxHeight?: string;
  limit?: number;
}) {
  const truncated = bytes.length > limit;
  // Slice before hexing — building the hex of a 2 MiB blob is itself the freeze.
  const shownHex = toHexString(truncated ? bytes.subarray(0, limit) : bytes);

  return (
    <div className="rounded-lg border bg-muted/30">
      <div className="flex items-center justify-between border-b px-3 py-1.5">
        <span className="text-xs font-medium text-muted-foreground">{label ?? "bytes"}</span>
        <div className="flex items-center gap-2">
          <span className="text-xs tabular-nums text-muted-foreground">
            {truncated ? `first ${limit} of ${bytes.length} bytes` : `${bytes.length} bytes`}
          </span>
          {/* Lazy: hexing the whole blob here would run on every render. */}
          <CopyButton value={() => toHexString(bytes)} label={label ?? "bytes"} />
        </div>
      </div>
      <div className="overflow-y-auto px-3 py-2" style={{ maxHeight }}>
        <HashValue value={shownHex} tone="muted" />
        {truncated && (
          <span className="ml-1 text-xs text-muted-foreground">
            … {bytes.length - limit} more bytes — copy to get all of them
          </span>
        )}
      </div>
    </div>
  );
}

/** Replace control characters so binary content cannot mangle the layout. */
function toPrintable(text: string): string {
  let out = "";
  for (const character of text) {
    const code = character.codePointAt(0)!;
    const isControl = (code < 0x20 && code !== 0x09 && code !== 0x0a) || code === 0x7f;
    out += isControl ? "·" : character;
  }
  return out;
}

/** Decoded text preview, for showing what a chunk actually contains. */
export function TextPreview({
  bytes,
  maxHeight = "6rem",
  limit = PREVIEW_TEXT_LIMIT,
}: {
  bytes: Uint8Array;
  maxHeight?: string;
  limit?: number;
}) {
  const truncated = bytes.length > limit;
  // Decode only what will be shown; decoding a whole 2 MiB chunk to throw most
  // of it away is the same freeze in a different costume.
  const decoded = new TextDecoder("utf-8", { fatal: false }).decode(truncated ? bytes.subarray(0, limit) : bytes);
  const text = toPrintable(decoded);

  return (
    <pre
      className="overflow-auto rounded-lg border bg-muted/30 px-3 py-2 font-mono text-xs break-all whitespace-pre-wrap"
      style={{ maxHeight }}
    >
      {text || <span className="text-muted-foreground">(empty)</span>}
      {truncated && (
        <span className="text-muted-foreground">
          {"\n"}… {bytes.length - limit} more bytes not shown
        </span>
      )}
    </pre>
  );
}

export { CopyButton, toHexString };
